import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  alpha,
  useTheme
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";

import {
  CHANGELOG,
  LATEST_RELEASE,
  LAST_SEEN_RELEASE_STORAGE_KEY
} from "src/handlers/changelog";
import useDialogEscape from "src/hooks/useDialogEscape";

// `ReleaseNotesModal` renders the changelog feed from
// `src/handlers/changelog.js`. The first entry is treated as the "current"
// release and rendered as a hero block at the top; everything older lives
// inside collapsed accordions so the modal stays scannable as the
// changelog grows.
//
// `manualOpen` lets the user re-open the modal from a settings menu /
// command palette without re-triggering the unseen-release detection.

function ReleaseEntry({ entry, isCurrent, defaultExpanded }) {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";

  if (isCurrent) {
    return (
      <Box
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: alpha(theme.palette.primary.main, isLight ? 0.32 : 0.4),
          bgcolor: alpha(theme.palette.primary.main, isLight ? 0.06 : 0.12),
          p: 2.25
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <RocketLaunchRoundedIcon
            sx={{ color: theme.palette.primary.main, fontSize: 18 }}
          />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            v{entry.version}
            {entry.title ? ` · ${entry.title}` : ""}
          </Typography>
          <Chip
            size="small"
            label="What's new"
            sx={{
              height: 22,
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.02em",
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText
            }}
          />
          {entry.date ? (
            <Typography variant="caption" sx={{ color: "text.secondary", ml: "auto" }}>
              {entry.date}
            </Typography>
          ) : null}
        </Stack>
        {Array.isArray(entry.highlights) && entry.highlights.length ? (
          <Stack spacing={1} sx={{ mb: entry.items?.length ? 1.5 : 0 }}>
            {entry.highlights.map((line) => (
              <Stack
                key={line}
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={{ color: "text.primary" }}
              >
                <StarRoundedIcon
                  sx={{
                    fontSize: 16,
                    mt: "2px",
                    color: theme.palette.primary.main,
                    flexShrink: 0
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {line}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : null}
        {Array.isArray(entry.items) && entry.items.length ? (
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {entry.items.map((line) => (
              <Box component="li" key={line} sx={{ color: "text.secondary", mb: 0.5 }}>
                <Typography variant="body2" component="span">
                  {line}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null}
      </Box>
    );
  }

  return (
    <Accordion
      disableGutters
      defaultExpanded={Boolean(defaultExpanded)}
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        "&:before": { display: "none" },
        "&.Mui-expanded": {
          borderColor: alpha(theme.palette.primary.main, isLight ? 0.24 : 0.32)
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon />}
        sx={{
          px: 1.75,
          minHeight: 44,
          "& .MuiAccordionSummary-content": { my: 0.5, alignItems: "center", gap: 1 }
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          v{entry.version}
        </Typography>
        {entry.title ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            · {entry.title}
          </Typography>
        ) : null}
        {entry.date ? (
          <Typography variant="caption" sx={{ color: "text.secondary", ml: "auto" }}>
            {entry.date}
          </Typography>
        ) : null}
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.75, pt: 0, pb: 1.5 }}>
        {Array.isArray(entry.highlights) && entry.highlights.length ? (
          <Stack spacing={0.5} sx={{ mb: entry.items?.length ? 1 : 0 }}>
            {entry.highlights.map((line) => (
              <Typography
                key={line}
                variant="body2"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {line}
              </Typography>
            ))}
          </Stack>
        ) : null}
        {Array.isArray(entry.items) && entry.items.length ? (
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {entry.items.map((line) => (
              <Box component="li" key={line} sx={{ color: "text.secondary", mb: 0.25 }}>
                <Typography variant="body2" component="span">
                  {line}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
}

export default function ReleaseNotesModal({ open, onClose, currentVersion }) {
  useDialogEscape({ enabled: open, onClose });

  const [latest, ...older] = CHANGELOG;
  const latestVersion = currentVersion || latest?.version || "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider"
        }
      }}
    >
      <DialogTitle
        sx={{
          pb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            What's new in NetBase
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You're now running v{latestVersion}.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close release notes">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 2.25 }}>
        <Stack spacing={2}>
          {latest ? (
            <ReleaseEntry entry={latest} isCurrent />
          ) : null}
          {older.length ? (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontWeight: 700,
                  color: "text.secondary",
                  mb: 1
                }}
              >
                Earlier releases
              </Typography>
              <Stack spacing={1}>
                {older.map((entry, idx) => (
                  <ReleaseEntry key={entry.version} entry={entry} defaultExpanded={idx === 0 ? false : false} />
                ))}
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.25, py: 1.5 }}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{ textTransform: "none" }}
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Lightweight gate component: mount once at the app root. Detects unseen
// releases via localStorage, opens the modal, and persists the dismiss.
//
// Other surfaces (settings menu, help command) can also dispatch
// `netbase:showReleaseNotes` to open the modal manually without changing
// the persisted "seen" state.
export function ReleaseNotesGate() {
  const [open, setOpen] = useState(false);

  const latestVersion = LATEST_RELEASE?.version || "";

  // Detect unseen release on mount. We treat a missing storage key as
  // "first-time visitor" and show the modal so the user knows what's in
  // the build they just loaded.
  useEffect(() => {
    if (!latestVersion) return;
    try {
      const seen = localStorage.getItem(LAST_SEEN_RELEASE_STORAGE_KEY);
      if (seen !== latestVersion) {
        setOpen(true);
      }
    } catch {
      // localStorage unavailable (private mode / SSR) — silently skip.
    }
  }, [latestVersion]);

  // Listen for manual triggers (e.g. command palette, footer link).
  useEffect(() => {
    const handler = () => {
      setOpen(true);
    };
    window.addEventListener("netbase:showReleaseNotes", handler);
    return () => window.removeEventListener("netbase:showReleaseNotes", handler);
  }, []);

  const close = useMemo(
    () => () => {
      setOpen(false);
      // Persist regardless of whether the open was automatic or manual —
      // dismissing the modal is the user telling us they've seen it.
      try {
        if (latestVersion) {
          localStorage.setItem(LAST_SEEN_RELEASE_STORAGE_KEY, latestVersion);
        }
      } catch {
        // ignore storage errors
      }
    },
    [latestVersion]
  );

  // Don't render the dialog at all when closed so the DOM stays clean and
  // there's no chance of a focus trap interfering with the rest of the app.
  if (!open) return null;

  return (
    <ReleaseNotesModal
      open={open}
      onClose={close}
      currentVersion={latestVersion}
    />
  );
}
